import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getClipAspectRatio } from "@air/utils-clips";
import classNames from "classnames";
import { memo, useCallback, useRef, useState } from "react";
import { VIDEO_SPRITESHEET_COLUMN_COUNT, VIDEO_SPRITESHEET_FRAME_COUNT, VIDEO_SPRITESHEET_ROW_COUNT } from "../constants/spritesheet";
import { useVideoHlsPreview } from "../hooks/useVideoHlsPreview";
import { useVideoPreview } from "../hooks/useVideoPreview";
import { VideoSeekbarPreview } from "./VideoSeekbarPreview";
import { VideoSeekbarPreviewHls } from "./VideoSeekbarPreviewHls";
import { VideoSeekbarPreviewSpritesheet } from "./VideoSeekbarPreviewSpritesheet";
import { VideoSeekbarProgress } from "./VideoSeekbarProgress";
import { VideoSeekbarProgressChapters } from "./VideoSeekbarProgressChapters";
export const VideoSeekbar = /*#__PURE__*/ memo((param)=>{
    let { className, clip, duration, onSeek, onSeekEnd, onSeekStart, processedPercentage, progressPercentage, renderTimestamp, chapters } = param;
    const inputRef = useRef(null);
    const [seekPosition, setSeekPosition] = useState(0);
    const [position, setPosition] = useState(0);
    const { onResetPreview, onUpdatePreview, onUpdatePreviewTimestamp, videoPreviewRef } = useVideoPreview();
    const { onUpdateVideo, videoRef } = useVideoHlsPreview({
        clip
    });
    const onPreviewStart = useCallback((event)=>{
        if (!inputRef.current || !clip.assets.spriteSheet && !videoRef) return null;
        const x = event.clientX - inputRef.current.getBoundingClientRect().left;
        const position = x / inputRef.current.offsetWidth;
        onUpdateVideo({
            duration: duration || clip.duration || 0,
            position
        });
        setSeekPosition(position);
        onUpdatePreview({
            x
        });
        onUpdatePreviewTimestamp({
            duration: duration || clip.duration || 0,
            position
        });
        setPosition(position);
    }, [
        clip.assets.spriteSheet,
        clip.duration,
        duration,
        onUpdatePreview,
        onUpdatePreviewTimestamp,
        onUpdateVideo,
        videoRef
    ]);
    const onPreviewEnd = useCallback(()=>{
        setSeekPosition(0);
        onResetPreview();
    }, [
        onResetPreview
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: classNames("relative grow select-none", className),
        onMouseEnter: onPreviewStart,
        onMouseMove: onPreviewStart,
        onMouseLeave: onPreviewEnd,
        style: {
            WebkitTouchCallout: "none"
        },
        children: [
            /*#__PURE__*/ _jsx(VideoSeekbarPreview, {
                ref: videoPreviewRef,
                children: clip.assets.spriteSheet ? /*#__PURE__*/ _jsx(VideoSeekbarPreviewSpritesheet, {
                    aspectRatio: getClipAspectRatio(clip),
                    columns: VIDEO_SPRITESHEET_COLUMN_COUNT,
                    position: position,
                    src: clip.assets.spriteSheet,
                    frameCount: VIDEO_SPRITESHEET_FRAME_COUNT,
                    rows: VIDEO_SPRITESHEET_ROW_COUNT
                }) : /*#__PURE__*/ _jsx(VideoSeekbarPreviewHls, {
                    clip: clip,
                    ref: videoRef
                })
            }),
            /*#__PURE__*/ _jsx("input", {
                className: "absolute bottom-0 h-6 w-full cursor-pointer appearance-none bg-transparent [&::-webkit-slider-thumb]:appearance-none",
                max: 1,
                min: 0,
                onChange: ()=>onSeek(seekPosition),
                onMouseDown: onSeekStart,
                onMouseUp: ()=>onSeekEnd(seekPosition),
                onTouchStart: onSeekStart,
                onTouchEnd: ()=>onSeekEnd(seekPosition),
                ref: inputRef,
                step: "any",
                type: "range",
                value: progressPercentage
            }),
            /*#__PURE__*/ _jsx(VideoSeekbarProgress, {
                color: "teal",
                percentage: progressPercentage * 100,
                children: chapters && (chapters === null || chapters === void 0 ? void 0 : chapters.length) > 0 && /*#__PURE__*/ _jsx(VideoSeekbarProgressChapters, {
                    chapters: chapters,
                    duration: duration
                })
            }),
            /*#__PURE__*/ _jsx(VideoSeekbarProgress, {
                className: "absolute top-1/2 -translate-y-1/2 bg-transparent",
                color: "grey",
                percentage: processedPercentage * 100,
                children: chapters && (chapters === null || chapters === void 0 ? void 0 : chapters.length) > 0 && /*#__PURE__*/ _jsx(VideoSeekbarProgressChapters, {
                    chapters: chapters,
                    duration: duration
                })
            }),
            renderTimestamp === null || renderTimestamp === void 0 ? void 0 : renderTimestamp()
        ]
    });
});
VideoSeekbar.displayName = "VideoSeekbar";
